import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores/auth';
import { userApi, tokenApi } from '../../api/client';
import type { TokenItem, CreatedToken } from '../../api/client';
import './UserSettingsPage.css';

const ALL_SCOPES = [
    { value: 'repo:read', label: '仓库读取', desc: '克隆、浏览仓库内容' },
    { value: 'repo:write', label: '仓库写入', desc: 'Push、创建分支、合并 PR' },
    { value: 'user:read', label: '用户信息读取', desc: '读取用户基本信息' },
    { value: 'group:read', label: '群组读取', desc: '浏览群组信息' },
    { value: 'admin', label: '管理员', desc: '访问管理控制台（高权限）' },
];

type Tab = 'profile' | 'tokens';

export function UserSettingsPage() {
    const { user, setUser, sessionId } = useAuthStore();
    const [activeTab, setActiveTab] = useState<Tab>('profile');

    // 个人信息
    const [email, setEmail] = useState(user?.email ?? '');
    const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? '');
    const [saving, setSaving] = useState(false);
    const [profileMsg, setProfileMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

    // Token 列表
    const [tokens, setTokens] = useState<TokenItem[]>([]);
    const [tokensLoading, setTokensLoading] = useState(false);

    // 创建 Token 表单
    const [showForm, setShowForm] = useState(false);
    const [newName, setNewName] = useState('');
    const [selectedScopes, setSelectedScopes] = useState<string[]>(['repo:read', 'repo:write']);
    const [expiresAt, setExpiresAt] = useState('');
    const [creating, setCreating] = useState(false);
    const [createdToken, setCreatedToken] = useState<CreatedToken | null>(null);
    const [copied, setCopied] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const loadTokens = useCallback(async () => {
        setTokensLoading(true);
        try {
            const list = await tokenApi.list();
            setTokens(list);
        } catch {
            // ignore
        } finally {
            setTokensLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'tokens') loadTokens();
    }, [activeTab, loadTokens]);

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setProfileMsg(null);
        try {
            const updated = await userApi.update({ email: email || undefined, avatarUrl: avatarUrl || undefined });
            if (sessionId) setUser(updated, sessionId);
            setProfileMsg({ type: 'ok', text: '个人信息已更新' });
        } catch (err: unknown) {
            setProfileMsg({ type: 'err', text: err instanceof Error ? err.message : '保存失败' });
        } finally {
            setSaving(false);
        }
    };

    const toggleScope = (scope: string) => {
        setSelectedScopes((prev) =>
            prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
        );
    };

    const handleCreateToken = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);
        if (!newName.trim()) { setFormError('请填写 Token 名称'); return; }
        if (selectedScopes.length === 0) { setFormError('请至少选择一个 Scope'); return; }
        setCreating(true);
        try {
            const result = await tokenApi.create({
                name: newName.trim(),
                scopes: selectedScopes,
                expiresAt: expiresAt || undefined,
            });
            setCreatedToken(result);
            setShowForm(false);
            setNewName('');
            setSelectedScopes(['repo:read', 'repo:write']);
            setExpiresAt('');
            await loadTokens();
        } catch (err: unknown) {
            setFormError(err instanceof Error ? err.message : '创建失败');
        } finally {
            setCreating(false);
        }
    };

    const handleRevoke = async (id: string, name: string) => {
        if (!window.confirm(`确定要删除 Token "${name}" 吗？删除后无法恢复。`)) return;
        try {
            await tokenApi.revoke(id);
            setTokens((prev) => prev.filter((t) => t.id !== id));
        } catch (err: unknown) {
            alert(err instanceof Error ? err.message : '删除失败');
        }
    };

    const handleCopy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // fallback
        }
    };

    const minExpiry = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

    return (
        <div className="user-settings-page">
            <div className="settings-sidebar">
                <div className="settings-user-info">
                    <div className="settings-avatar">
                        {user?.avatarUrl ? (
                            <img src={user.avatarUrl} alt={user.username} />
                        ) : (
                            <span>{user?.username?.[0]?.toUpperCase()}</span>
                        )}
                    </div>
                    <div>
                        <div className="settings-username">{user?.username}</div>
                        <div className="settings-email">{user?.email}</div>
                    </div>
                </div>
                <nav className="settings-nav">
                    <button
                        className={`settings-nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        个人信息
                    </button>
                    <button
                        className={`settings-nav-item ${activeTab === 'tokens' ? 'active' : ''}`}
                        onClick={() => setActiveTab('tokens')}
                    >
                        访问令牌
                    </button>
                </nav>
            </div>

            <div className="settings-content">
                {activeTab === 'profile' && (
                    <section className="settings-panel">
                        <h2>个人信息</h2>
                        <form onSubmit={handleSaveProfile}>
                            <div className="field-group">
                                <label>用户名</label>
                                <input type="text" value={user?.username ?? ''} disabled className="input-disabled" />
                                <span className="field-hint">用户名不可修改</span>
                            </div>
                            <div className="field-group">
                                <label>邮箱</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                />
                            </div>
                            <div className="field-group">
                                <label>头像 URL</label>
                                <input
                                    type="url"
                                    value={avatarUrl}
                                    onChange={(e) => setAvatarUrl(e.target.value)}
                                    placeholder="https://example.com/avatar.png"
                                />
                            </div>
                            {profileMsg && (
                                <div className={`settings-msg ${profileMsg.type}`}>{profileMsg.text}</div>
                            )}
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? '保存中…' : '保存'}
                            </button>
                        </form>
                    </section>
                )}

                {activeTab === 'tokens' && (
                    <section className="settings-panel">
                        <div className="panel-header">
                            <div>
                                <h2>访问令牌（PAT）</h2>
                                <p className="panel-desc">
                                    Personal Access Token 用于脚本、CI/CD 或 Git HTTPS 认证，可替代密码使用。
                                </p>
                            </div>
                            {!showForm && !createdToken && (
                                <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                                    + 新建 Token
                                </button>
                            )}
                        </div>

                        {/* 新建 Token 表单 */}
                        {showForm && (
                            <form className="token-form" onSubmit={handleCreateToken}>
                                <h3>新建 Token</h3>
                                <div className="field-group">
                                    <label>Token 名称 <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        placeholder="例：ci-deploy、git-push"
                                        autoFocus
                                    />
                                </div>
                                <div className="field-group">
                                    <label>权限范围（Scope）<span className="required">*</span></label>
                                    <div className="scope-list">
                                        {ALL_SCOPES.map((s) => (
                                            <label key={s.value} className="scope-item">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedScopes.includes(s.value)}
                                                    onChange={() => toggleScope(s.value)}
                                                />
                                                <div className="scope-info">
                                                    <span className="scope-name">{s.label}</span>
                                                    <span className="scope-value">{s.value}</span>
                                                    <span className="scope-desc">{s.desc}</span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="field-group">
                                    <label>过期时间（可选）</label>
                                    <input
                                        type="date"
                                        value={expiresAt}
                                        min={minExpiry}
                                        onChange={(e) => setExpiresAt(e.target.value)}
                                    />
                                    <span className="field-hint">不填写则永不过期</span>
                                </div>
                                {formError && <div className="settings-msg err">{formError}</div>}
                                <div className="form-actions">
                                    <button type="submit" className="btn btn-primary" disabled={creating}>
                                        {creating ? '创建中…' : '创建 Token'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => { setShowForm(false); setFormError(null); }}
                                    >
                                        取消
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* 新建成功，展示 Token 值 */}
                        {createdToken && (
                            <div className="token-reveal">
                                <div className="token-reveal-header">
                                    <span className="token-reveal-icon">✓</span>
                                    <strong>Token 创建成功 — 请立即复制保存，关闭后将无法再次查看</strong>
                                </div>
                                <div className="token-value-row">
                                    <code className="token-value">{createdToken.token}</code>
                                    <button
                                        className={`btn-copy ${copied ? 'copied' : ''}`}
                                        onClick={() => handleCopy(createdToken.token)}
                                    >
                                        {copied ? '已复制' : '复制'}
                                    </button>
                                </div>
                                <div className="token-usage">
                                    <p className="field-hint">Git HTTPS 使用方式：</p>
                                    <code>git clone https://{user?.username}:{createdToken.token}@api.gitflare.org/owner/repo.git</code>
                                </div>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setCreatedToken(null)}
                                >
                                    我已保存，关闭
                                </button>
                            </div>
                        )}

                        {/* Token 列表 */}
                        {tokensLoading ? (
                            <div className="tokens-loading">加载中…</div>
                        ) : tokens.length === 0 ? (
                            <div className="tokens-empty">
                                <p>还没有访问令牌</p>
                                <p className="field-hint">创建 Token 后可用于 Git HTTPS 认证、API 调用等场景</p>
                            </div>
                        ) : (
                            <ul className="token-list">
                                {tokens.map((t) => (
                                    <li key={t.id} className="token-item">
                                        <div className="token-meta">
                                            <span className="token-name">{t.name}</span>
                                            <div className="token-scopes">
                                                {t.scopes.map((s) => (
                                                    <span key={s} className="scope-badge">{s}</span>
                                                ))}
                                            </div>
                                            <span className="token-date">
                                                创建于 {new Date(t.createdAt).toLocaleDateString('zh-CN')}
                                                {t.expiresAt && (
                                                    <> · 过期于 {new Date(t.expiresAt).toLocaleDateString('zh-CN')}</>
                                                )}
                                            </span>
                                        </div>
                                        <button
                                            className="btn btn-danger-sm"
                                            onClick={() => handleRevoke(t.id, t.name)}
                                        >
                                            删除
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}
